import Axios from 'axios';
import { getBaseUrlAtlance } from '../helpers/environment';

// Separate instance from services/axios: the Atlance API is another origin and
// must never receive the auth-service Bearer token — the signed signup token in
// the URL is the only credential.
const AtlanceAxios = Axios.create({
  baseURL: getBaseUrlAtlance(),
  timeout: 30 * 1000,
  headers: {
    accept: 'application/json',
    'content-type': 'application/json',
  },
});

// 410 = this signup is over: the link expired, or the deal behind it was set
// to Closed Lost in Salesforce while the wizard was open. Any call that meets
// it tells the wizard, which re-reads /state and leaves the open step. /state
// itself is skipped — it is the re-read, and must not trigger another one.
export const SIGNUP_CLOSED_EVENT = 'signup:closed';

AtlanceAxios.interceptors.response.use(undefined, err => {
  if (err?.response?.status === 410 && !err.config?.url?.endsWith('/state')) {
    window.dispatchEvent(new Event(SIGNUP_CLOSED_EVENT));
  }
  return Promise.reject(err);
});

/**
 * Signup wizard: current state (boot + every refresh)
 */
export async function getSignupState(token) {
  const response = await AtlanceAxios.get(
    `/api/referrals/signup/${encodeURIComponent(token)}/state`,
  );
  return response.data;
}

/**
 * Signup wizard: landing-card contact edits (POC name/email + extra CCs)
 */
export async function submitSignupContact(token, params) {
  const response = await AtlanceAxios.post(
    `/api/referrals/signup/${encodeURIComponent(token)}/contact`,
    params,
  );
  return response.data;
}

/**
 * Signup wizard: submit identity step
 */
export async function submitSignupIdentity(token, params) {
  const response = await AtlanceAxios.post(
    `/api/referrals/signup/${encodeURIComponent(token)}/identity`,
    params,
  );
  return response.data;
}

/**
 * Signup wizard: submit billing address (server also builds the order form)
 */
export async function submitSignupDetails(token, params) {
  const response = await AtlanceAxios.post(
    `/api/referrals/signup/${encodeURIComponent(token)}/details`,
    params,
  );
  return response.data;
}

/**
 * Signup wizard: create or reuse the embedded PandaDoc signing session
 */
export async function createSignSession(token) {
  const response = await AtlanceAxios.post(
    `/api/referrals/signup/${encodeURIComponent(token)}/sign-session`,
  );
  return response.data;
}

/**
 * Signup wizard: create or reuse the Stripe SetupIntent for card on file
 */
export async function createSetupIntent(token) {
  const response = await AtlanceAxios.post(
    `/api/referrals/signup/${encodeURIComponent(token)}/setup-intent`,
  );
  return response.data;
}

/**
 * Signup wizard: poll signing/card progress
 */
export async function getSignupStatus(token) {
  const response = await AtlanceAxios.get(
    `/api/referrals/signup/${encodeURIComponent(token)}/status`,
  );
  return response.data;
}

/**
 * The merchant's Order Form PDF — the SIGNED document once completed.
 * A plain URL (opened in a new tab), token-gated server-side.
 */
export function orderFormPdfUrl(token) {
  return `${getBaseUrlAtlance()}/api/referrals/signup/${encodeURIComponent(
    token,
  )}/order-form.pdf`;
}

// Long details are raw upstream dumps (PandaDoc/Stripe payloads), not
// user-facing copy — fall back to the generic message for those.
const MAX_DETAIL_LENGTH = 140;

/**
 * Human message from an Atlance error response ({ detail, request_id })
 */
export function getSignupErrorMessage(
  err,
  fallback = 'Something went wrong. Please try again in a moment.',
) {
  const detail = err?.response?.data?.detail;
  if (
    detail &&
    typeof detail === 'string' &&
    detail.length <= MAX_DETAIL_LENGTH
  ) {
    return detail;
  }

  return fallback;
}

/**
 * Request id from an Atlance error response — quote it when reporting issues
 */
export function getSignupRequestId(err) {
  return err?.response?.data?.request_id;
}
